import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCompanyConfigs } from "../../../redux/slices/CompanyConfigSlice";
import CompanyConfigDataService from "../../../services/CompanyConfigDataService";
import { notify } from "../../../assets/utils/UtilsFunctons";
import Loader from "../../loader/Loader";

const COMPANY_LABELS = { 0: "Diggers", 1: "Finaura" };

const LogoCell = ({ id, type }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [uploading, setUploading] = useState(false);

  const fetchImage = useCallback(async () => {
    try {
      const res = type === "logo"
        ? await CompanyConfigDataService.getLogo(id)
        : await CompanyConfigDataService.getMiniLogo(id);
      if (res.data && res.data.length > 0) {
        setImageUrl(res.data[0].url);
      } else {
        setImageUrl(null);
      }
    } catch {
      setImageUrl(null);
    }
  }, [id, type]);

  useEffect(() => {
    fetchImage();
  }, [fetchImage]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      if (type === "logo") {
        await CompanyConfigDataService.uploadLogo(id, file);
      } else {
        await CompanyConfigDataService.uploadMiniLogo(id, file);
      }
      notify(1, "Image mise à jour !");
      await fetchImage();
    } catch {
      notify(0, "Erreur lors de l'upload");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    try {
      if (type === "logo") {
        await CompanyConfigDataService.deleteLogo(id);
      } else {
        await CompanyConfigDataService.deleteMiniLogo(id);
      }
      notify(1, "Image supprimée !");
      setImageUrl(null);
    } catch {
      notify(0, "Erreur lors de la suppression");
    }
  };

  return (
    <div className="d-flex align-items-center gap-2">
      {imageUrl ? (
        <>
          <img
            src={imageUrl}
            alt={type}
            style={{ height: type === "logo" ? 32 : 22, objectFit: "contain" }}
          />
          <button className="btn btn-sm btn-outline-danger" onClick={handleDelete}>
            <i className="bx bx-trash" />
          </button>
        </>
      ) : (
        <span className="text-muted fst-italic">—</span>
      )}
      <label className="btn btn-sm btn-outline-secondary mb-0" style={{ cursor: "pointer" }}>
        {uploading ? "..." : <i className="bx bx-upload" />}
        <input type="file" accept="image/*" style={{ display: "none" }} onChange={handleUpload} />
      </label>
    </div>
  );
};

const CompanyConfigManagement = () => {
  const dispatch = useDispatch();
  const { companyConfigs, loading_companyConfigs } = useSelector((s) => s.companyConfig);

  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    dispatch(getCompanyConfigs());
  }, []);

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditValues({
      id: item.id,
      name: item.name,
      label: item.label,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValues({});
  };

  const handleChange = (field, value) => {
    setEditValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await CompanyConfigDataService.update(editValues);
      notify(1, "Configuration mise à jour avec succès !");
      setEditingId(null);
      dispatch(getCompanyConfigs());
    } catch (error) {
      notify(0, error?.response?.data?.Message ?? "Une erreur est survenue");
    } finally {
      setSaving(false);
    }
  };

  if (loading_companyConfigs) return <Loader />;
  if (!companyConfigs?.data) return <p>Aucune donnée disponible.</p>;

  const rows = companyConfigs.data;

  return (
    <div>
      <h5 className="mb-3">
        <strong>Configuration des sociétés</strong>
      </h5>
      <p className="text-muted mb-4">
        Personnalisez le nom, le label et les logos pour chaque société.
      </p>

      <div className="table-responsive">
        <table className="table table-bordered table-hover align-middle">
          <thead className="table-light">
            <tr>
              <th>Nom</th>
              <th>Label</th>
              <th>Logo</th>
              <th>Mini-logo</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((item) => {
              const isEditing = editingId === item.id;
              return (
                <tr key={item.id}>

                  {/* Nom */}
                  <td>
                    {isEditing ? (
                      <input
                        className="form-control form-control-sm"
                        value={editValues.name}
                        onChange={(e) => handleChange("name", e.target.value)}
                      />
                    ) : (
                      item.name
                    )}
                  </td>

                  {/* Label */}
                  <td>
                    {isEditing ? (
                      <input
                        className="form-control form-control-sm"
                        value={editValues.label}
                        onChange={(e) => handleChange("label", e.target.value)}
                      />
                    ) : (
                      item.label
                    )}
                  </td>

                  {/* Logo */}
                  <td><LogoCell id={item.id} type="logo" /></td>

                  {/* Mini-logo */}
                  <td><LogoCell id={item.id} type="minilogo" /></td>

                  {/* Actions */}
                  <td>
                    {isEditing ? (
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm btn-success"
                          onClick={handleSave}
                          disabled={saving}
                        >
                          {saving ? "..." : <i className="bx bx-check" />}
                        </button>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={cancelEdit}
                          disabled={saving}
                        >
                          <i className="bx bx-x" />
                        </button>
                      </div>
                    ) : (
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => startEdit(item)}
                      >
                        <i className="bx bx-edit-alt" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CompanyConfigManagement;